/**
 * Aspect-ratio-constrained upload + crop primitive. Accepts an image via
 * drag/drop or the native file picker, shows a locked-ratio crop frame the
 * user can pan (drag, or arrow keys) and zoom, and on confirm emits a
 * Blob/File already cropped to the exact ratio, resized to `maxDimension`,
 * and compressed — the "optimized for web" step done once, here, so no
 * downstream consumer has to repeat it.
 *
 * Purity contract (mirrors `schmancy-file-viewer`): no Firebase, no upload
 * logic, no knowledge of storage paths. The component only ever emits a
 * ready-to-upload `Blob`/`File` via the `crop` event — the caller decides
 * where it goes.
 */
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { parseAspectRatio } from '@schmancy/utils'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { exhaustMap, filter, fromEvent, merge, Subject, switchMap, takeUntil, tap } from 'rxjs'
import '../button/button'
import '../icons/icon'
import '../skeleton/skeleton'
import '../typography/typography'
import '@schmancy/form/fields/range/range'

export type SchmancyImageCropEvent = CustomEvent<{
	blob: Blob
	file: File
	width: number
	height: number
	ratio: string
}>

type PickedSource = { readonly kind: 'file'; readonly file: File } | { readonly kind: 'url'; readonly url: string }

type DecodeResult =
	| {
			readonly ok: true
			readonly url: string
			readonly owned: boolean
			readonly width: number
			readonly height: number
			readonly name: string
	  }
	| { readonly ok: false; readonly message: string }

type CropResult =
	| {
			readonly ok: true
			readonly blob: Blob
			readonly width: number
			readonly height: number
			readonly fileName: string
	  }
	| { readonly ok: false; readonly message: string }

const NUDGE_PX = 12

/**
 * @element schmancy-image-crop
 * @fires crop - `{ blob, file, width, height, ratio }` — the finished, ratio-locked, resized, compressed result.
 */
@schmancyElement('schmancy-image-crop')
export class SchmancyImageCrop extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	/** Target ratio as "W:H" — e.g. "2:3", "1:1", "16:9". */
	@property({ type: String }) ratio = '1:1'
	/** Forwarded to the native file input's `accept`. */
	@property({ type: String }) accept = 'image/*'
	/** Long-edge cap (px) for the emitted result — the "optimized for web" resize. */
	@property({ type: Number, attribute: 'max-dimension' }) maxDimension = 1600
	/** Encoder quality, 0–1. */
	@property({ type: Number }) quality = 0.85
	@property({ type: String, attribute: 'output-type' }) outputType: 'image/jpeg' | 'image/webp' = 'image/jpeg'
	/** Max zoom multiplier over the cover-fit scale. */
	@property({ type: Number, attribute: 'max-zoom' }) maxZoom = 3
	/** What the cropped subject is, for the reposition control's accessible name — e.g. "poster", "avatar". */
	@property({ type: String }) label = 'image'
	/** Start already loaded from an existing image (edit mode) instead of empty. Cross-origin URLs need CORS for the confirm step to encode successfully. */
	@property({ type: String, attribute: 'initial-image-url' }) initialImageUrl: string | null = null

	@state() private phase: 'empty' | 'loading' | 'ready' = 'empty'
	@state() private loadError: string | null = null
	@state() private objectUrl: string | null = null
	@state() private zoom = 1
	@state() private panX = 50
	@state() private panY = 50

	private objectUrlOwned = false
	private naturalWidth = 0
	private naturalHeight = 0
	private pickedFileName = 'image'

	private readonly frameRef = createRef<HTMLDivElement>()
	private readonly imgRef = createRef<HTMLImageElement>()

	private readonly picked$ = new Subject<PickedSource>()
	private readonly panStart$ = new Subject<PointerEvent>()
	private readonly panNudge$ = new Subject<readonly [number, number]>()
	private readonly zoomInput$ = new Subject<number>()
	private readonly cropConfirm$ = new Subject<void>()

	override connectedCallback(): void {
		super.connectedCallback()

		merge(
			this.picked$.pipe(
				tap(() => {
					this.phase = 'loading'
					this.loadError = null
				}),
				switchMap(async (source): Promise<DecodeResult> => {
					let objectUrl: string | null = null
					let url: string
					if (source.kind === 'file') {
						objectUrl = URL.createObjectURL(source.file)
						url = objectUrl
					} else {
						url = source.url
					}
					const name = source.kind === 'file' ? source.file.name : 'image'
					try {
						const img = new Image()
						if (source.kind === 'url' && !/^(blob:|data:)/.test(source.url)) img.crossOrigin = 'anonymous'
						img.src = url
						await img.decode()
						return {
							ok: true,
							url,
							owned: objectUrl !== null,
							width: img.naturalWidth,
							height: img.naturalHeight,
							name,
						}
					} catch {
						if (objectUrl) URL.revokeObjectURL(objectUrl)
						return { ok: false, message: 'That file could not be read as an image.' }
					}
				}),
				tap((result: DecodeResult) => {
					// strict:false in this project's tsconfig means TS 6.0.3 only narrows
					// this discriminated union via an explicit `=== false`/`=== true`
					// equality check — `!result.ok` leaves `result` unnarrowed here.
					if (result.ok === false) {
						this.phase = 'empty'
						this.loadError = result.message
						return
					}
					if (this.objectUrl && this.objectUrlOwned) URL.revokeObjectURL(this.objectUrl)
					this.objectUrl = result.url
					this.objectUrlOwned = result.owned
					this.naturalWidth = result.width
					this.naturalHeight = result.height
					this.pickedFileName = result.name
					this.zoom = 1
					this.panX = 50
					this.panY = 50
					this.phase = 'ready'
				}),
			),
			this.panStart$.pipe(
				tap(e => e.preventDefault()),
				exhaustMap(down => {
					let lastX = down.clientX
					let lastY = down.clientY
					return fromEvent<PointerEvent>(window, 'pointermove').pipe(
						tap(move => {
							this.nudgePan(move.clientX - lastX, move.clientY - lastY)
							lastX = move.clientX
							lastY = move.clientY
						}),
						takeUntil(merge(fromEvent(window, 'pointerup'), fromEvent(window, 'pointercancel'))),
					)
				}),
			),
			this.panNudge$.pipe(tap(([dx, dy]) => this.nudgePan(dx, dy))),
			this.zoomInput$.pipe(
				tap(z => {
					this.zoom = z
				}),
			),
			this.cropConfirm$.pipe(
				filter(() => this.phase === 'ready'),
				switchMap(async (): Promise<CropResult> => {
					const frame = this.frameRef.value
					const img = this.imgRef.value
					if (!frame || !img || !this.naturalWidth || !this.naturalHeight) {
						return { ok: false, message: 'Nothing to crop yet.' }
					}
					const [rw, rh] = parseAspectRatio(this.ratio)
					const { width: cw, height: ch } = frame.getBoundingClientRect()
					const s0 = Math.max(cw / this.naturalWidth, ch / this.naturalHeight)
					const scale = s0 * this.zoom
					const offsetX = (cw - this.naturalWidth * s0) * (this.panX / 100)
					const offsetY = (ch - this.naturalHeight * s0) * (this.panY / 100)
					// object-position places the cover-fit image, then transform:scale(zoom)
					// magnifies around the frame's own center — invert both to recover the
					// visible source rectangle in natural-image pixels.
					const zoomInsetX = (cw / 2) * (1 - 1 / this.zoom)
					const zoomInsetY = (ch / 2) * (1 - 1 / this.zoom)
					const cropW = cw / scale
					const cropH = ch / scale
					const cropX = Math.min(Math.max((zoomInsetX - offsetX) / s0, 0), this.naturalWidth - cropW)
					const cropY = Math.min(Math.max((zoomInsetY - offsetY) / s0, 0), this.naturalHeight - cropH)
					const outW = rw >= rh ? this.maxDimension : Math.round(this.maxDimension * (rw / rh))
					const outH = rh >= rw ? this.maxDimension : Math.round(this.maxDimension * (rh / rw))
					const canvas = document.createElement('canvas')
					canvas.width = outW
					canvas.height = outH
					const ctx = canvas.getContext('2d')
					if (!ctx) return { ok: false, message: 'This browser cannot encode the cropped image.' }
					ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, outW, outH)
					const ext = this.outputType === 'image/webp' ? 'webp' : 'jpg'
					const base = this.pickedFileName.replace(/\.[^./]+$/, '')
					try {
						const blob = await new Promise<Blob>((resolve, reject) =>
							canvas.toBlob(b => (b ? resolve(b) : reject(new Error('encode failed'))), this.outputType, this.quality),
						)
						return { ok: true, blob, width: outW, height: outH, fileName: `${base}-${rw}x${rh}.${ext}` }
					} catch {
						return { ok: false, message: 'This browser could not encode the cropped image.' }
					}
				}),
				tap((result: CropResult) => {
					if (result.ok === false) {
						this.loadError = result.message
						return
					}
					const file = new File([result.blob], result.fileName, { type: this.outputType })
					this.dispatchEvent(
						new CustomEvent('crop', {
							detail: { blob: result.blob, file, width: result.width, height: result.height, ratio: this.ratio },
							bubbles: true,
							composed: true,
						}),
					)
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()

		// AFTER the subscribe, not before it: `picked$` is a plain Subject, so an
		// emission with no subscriber attached is dropped and the component sits
		// on its dropzone forever holding a source it was handed.
		if (this.initialImageUrl) this.picked$.next({ kind: 'url', url: this.initialImageUrl })
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		if (this.objectUrl && this.objectUrlOwned) URL.revokeObjectURL(this.objectUrl)
	}

	/** Converts a screen-pixel drag/nudge delta into a pan-percentage change, following the pointer 1:1 on screen regardless of zoom. */
	private nudgePan(dxScreen: number, dyScreen: number): void {
		const frame = this.frameRef.value
		if (!frame || !this.naturalWidth || !this.naturalHeight) return
		const { width: cw, height: ch } = frame.getBoundingClientRect()
		const s0 = Math.max(cw / this.naturalWidth, ch / this.naturalHeight)
		const rangeX = cw - this.naturalWidth * s0
		const rangeY = ch - this.naturalHeight * s0
		const offsetX = rangeX * (this.panX / 100) + dxScreen / this.zoom
		const offsetY = rangeY * (this.panY / 100) + dyScreen / this.zoom
		this.panX = rangeX === 0 ? 50 : Math.min(100, Math.max(0, (offsetX / rangeX) * 100))
		this.panY = rangeY === 0 ? 50 : Math.min(100, Math.max(0, (offsetY / rangeY) * 100))
	}

	/** Clears the loaded image and returns to the empty/dropzone phase. Public so a caller can reset the control after using its result (e.g. once the upload it triggered succeeds). */
	reset(): void {
		if (this.objectUrl && this.objectUrlOwned) URL.revokeObjectURL(this.objectUrl)
		this.objectUrl = null
		this.objectUrlOwned = false
		this.phase = 'empty'
		this.loadError = null
		this.panX = 50
		this.panY = 50
		this.zoom = 1
	}

	override render() {
		const [rw, rh] = parseAspectRatio(this.ratio)

		if (this.phase === 'loading') {
			return html`
				<div
					role="status"
					aria-busy="true"
					aria-label="Loading ${this.label}"
					class="w-full overflow-hidden rounded-2xl"
					style="aspect-ratio: ${rw} / ${rh}"
				>
					<schmancy-skeleton class="h-full w-full"></schmancy-skeleton>
				</div>
			`
		}

		if (this.phase === 'ready') {
			return html`
				<div class="flex flex-col items-stretch gap-3">
					<div
						${ref(this.frameRef)}
						role="group"
						tabindex="0"
						aria-label="Reposition the ${this.label} — drag, or use arrow keys"
						class="relative w-full touch-none overflow-hidden rounded-2xl border-2 border-dashed border-primary-default cursor-grab focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-default active:cursor-grabbing"
						style="aspect-ratio: ${rw} / ${rh}"
						@pointerdown=${(e: PointerEvent) => this.panStart$.next(e)}
						@keydown=${(e: KeyboardEvent) => {
							const step = NUDGE_PX
							if (e.key === 'ArrowLeft') {
								e.preventDefault()
								this.panNudge$.next([-step, 0])
							} else if (e.key === 'ArrowRight') {
								e.preventDefault()
								this.panNudge$.next([step, 0])
							} else if (e.key === 'ArrowUp') {
								e.preventDefault()
								this.panNudge$.next([0, -step])
							} else if (e.key === 'ArrowDown') {
								e.preventDefault()
								this.panNudge$.next([0, step])
							}
						}}
					>
						<img
							${ref(this.imgRef)}
							src=${this.objectUrl ?? ''}
							alt=""
							class="h-full w-full select-none object-cover"
							style="object-position: ${this.panX}% ${this.panY}%; transform: scale(${this.zoom})"
						/>
					</div>

					<schmancy-range
						label="Zoom"
						.min=${1}
						.max=${this.maxZoom}
						.step=${0.01}
						.value=${this.zoom}
						@change=${(e: CustomEvent<{ value: number }>) => {
							if (e.detail.value === this.zoom) return
							this.zoomInput$.next(e.detail.value)
						}}
					></schmancy-range>

					${when(
						!!this.loadError,
						() =>
							html`<schmancy-typography type="body" token="xs" role="alert" class="text-error-default">
								${this.loadError}
							</schmancy-typography>`,
					)}

					<div class="flex w-full items-center justify-between gap-2">
						<schmancy-button variant="text" @click=${() => this.reset()}>Choose a different photo</schmancy-button>
						<schmancy-button variant="filled" @click=${() => this.cropConfirm$.next()}>Use this photo</schmancy-button>
					</div>
				</div>
			`
		}

		return html`
			<div
				class="relative flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-control-border bg-surface-container px-6 py-10 text-center transition-colors hover:border-primary-default focus-within:border-primary-default"
				style="aspect-ratio: ${rw} / ${rh}"
				@dragover=${(e: DragEvent) => e.preventDefault()}
				@drop=${(e: DragEvent) => {
					e.preventDefault()
					const file = e.dataTransfer?.files?.[0]
					if (!file) return
					if (!file.type.startsWith('image/')) {
						this.loadError = 'Choose an image file.'
						return
					}
					this.picked$.next({ kind: 'file', file })
				}}
			>
				<schmancy-icon class="text-3xl text-surface-onVariant" aria-hidden="true">add_photo_alternate</schmancy-icon>
				<schmancy-typography type="body" token="sm">Drag an image here, or click to browse</schmancy-typography>
				<schmancy-typography type="body" token="xs" class="text-surface-onVariant">
					You'll crop it to ${rw}:${rh} next.
				</schmancy-typography>
				${when(
					!!this.loadError,
					() =>
						html`<schmancy-typography type="body" token="xs" role="alert" class="text-error-default">
							${this.loadError}
						</schmancy-typography>`,
				)}
				<input
					type="file"
					accept=${this.accept}
					aria-label="Choose ${this.label} to upload"
					class="absolute inset-0 h-full w-full cursor-pointer opacity-0"
					@change=${(e: Event) => {
						const input = e.target as HTMLInputElement
						const file = input.files?.[0]
						input.value = ''
						if (!file) return
						if (!file.type.startsWith('image/')) {
							this.loadError = 'Choose an image file.'
							return
						}
						this.picked$.next({ kind: 'file', file })
					}}
				/>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-image-crop': SchmancyImageCrop
	}
}
